import { InfiniteData, QueryClient } from '@tanstack/query-core'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { produce } from 'immer'
import queryString from 'query-string'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  ExpenseDetailsModel,
  TransactionListModel,
} from 'src/models/transaction.models'
import { AdditionalFieldsResponse } from 'src/queries/organizations/additionalFields.queries'
import type { ExpenseCategoriesResponse } from 'src/queries/organizations/expenseCategories.network'
import {
  CATEGORIES_QK,
  EXPENSE_CATEGORIES_LABELS_BULK_TEMPLATE_QK,
  EXPENSE_CATEGORIES_LABELS_QK,
  EXPENSE_DATA_QK,
  orgQueryKeys,
} from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getListQueryKeys,
  getNextExtendPageParam,
  MutationMethod,
  RequestConfig,
  useExtendInfiniteQuery,
  useExtendMutation,
  UseExtendMutationVariablesBody,
} from 'src/queries/queries.utils'
import { BackendErrorPayload } from 'src/utils/error.utils'

// Reproduce useExtendQuery's error contract: expose the backend payload from
// the axios error's response (undefined when there is no response), NOT a
// normalized always-truthy object. Consumers gate UI on this exact shape.
const toBackendError = (
  error: unknown,
): BackendErrorPayload<string> | undefined =>
  (error as AxiosError<BackendErrorPayload<string>> | null)?.response?.data

export enum ExpenseCategoryType {
  Account = 'ACCOUNT',
  Expense = 'EXPENSE',
  Customer = 'CUSTOMER',
  Vendor = 'VENDOR',
}

export interface ExpenseCategoryResponse {
  id: string
  name: string
  code: string
  active: boolean
  requiredForVirtualCards: boolean
  requiredForTransactions: boolean
  freeTextAllowed: boolean
  integratorEnabled: boolean
  integratorFieldNumber?: number
  editable: boolean
  type: ExpenseCategoryType
  hasLabels: boolean
}

export enum ExpenseCategorySortField {
  ActiveCodeAsc = 'activeCodeAsc',
  ActiveCodeDesc = 'activeCodeDesc',
  // TODO: backend currently has these as activeLabelNameAsc and activeLabelNameDesc
  ActiveCategoryNameAsc = 'activeLabelNameAsc',
  ActiveCategoryNameDesc = 'activeLabelNameDesc',
  // use these with SortDirection
  Name = 'name',
  Code = 'code',
}

export type ExpenseCategoryFiltersRequestParams = {
  search?: string
  active?: boolean
  requiredForVirtualCards?: boolean
  requiredForTransactions?: boolean
  transactionId?: string
  sortField?: ExpenseCategorySortField
  sortDirection?: 'ASC' | 'DESC'
}

export const activeAlphabeticalExpenseCategoryFiltersRequestParams: ExpenseCategoryFiltersRequestParams =
  {
    active: true,
    sortField: ExpenseCategorySortField.Name,
    sortDirection: 'ASC',
  }

interface QueryExpenseCategoriesOptions
  extends ExtendBaseQueryOptions<
    { expenseCategories: ExpenseCategoryResponse[] },
    ExpenseCategoryResponse[]
  > {
  filters: ExpenseCategoryFiltersRequestParams
  additionalKey?: string
}

const selectExpenseCategories = (data: {
  expenseCategories: ExpenseCategoryResponse[]
}) => {
  return data.expenseCategories.map((category) => ({
    ...category,
    // TODO: backend currently has an issue correctly identifying hasLabels
    // for ERP connected categories we can make the safe assumption that if a
    // user has connected, then it would have labels. The `hasLabels: false`
    // value is only used for free text anyways, which is not supported for
    // ERP connected orgs
    hasLabels:
      category.type !== ExpenseCategoryType.Expense ? true : category.hasLabels,
  }))
}
const selectTransactionCustomFields = (
  data: ExpenseCategoriesResponse,
): AdditionalFieldsResponse[] =>
  data.expenseCategories.map((category) => ({
    id: category.id,
    name: category.name,
    active: category.active,
    required: category.requiredForTransactions,
    editable: category.editable ?? true,
  }))

// Load the network module lazily (inside queryFn), never at module init. A
// static import pulls requestConfig -> clientStore (which calls configureStore
// at module scope), and this file is reachable from createRootReducer, so an
// eager import re-introduces the store-init circular-require that leaves
// createRootReducer's default export undefined under jest (see extend-app commit
// 1479146). Deferring to query time — after the store has initialized — closes it.
const loadExpenseCategoriesNetwork = () =>
  import('src/queries/organizations/expenseCategories.network')

export const expenseCategoryQueries = {
  list: (
    filters: ExpenseCategoryFiltersRequestParams,
    additionalKey?: string,
  ) =>
    queryOptions({
      queryKey: orgQueryKeys.expenseData(filters, additionalKey),
      // hardcoding sort for now
      queryFn: async () =>
        (await loadExpenseCategoriesNetwork()).getExpenseCategories(filters),
      select: selectExpenseCategories,
    }),
  detail: (expenseCategoryId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.expenseDataDetail(expenseCategoryId),
      queryFn: async () =>
        (await loadExpenseCategoriesNetwork()).getExpenseCategory(
          expenseCategoryId!,
        ),
      enabled: expenseCategoryId !== undefined,
    }),
  label: (expenseCategoryId: string | undefined, labelId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.expenseDataLabelDetail(expenseCategoryId, labelId),
      queryFn: async () =>
        (await loadExpenseCategoriesNetwork()).getExpenseCategoryLabel(
          expenseCategoryId!,
          labelId!,
        ),
      enabled: expenseCategoryId !== undefined && labelId !== undefined,
    }),
  transactionCustomFields: (transactionId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.transactionCustomFields(transactionId),
      queryFn: async () =>
        (await loadExpenseCategoriesNetwork()).getTransactionCustomFields(
          transactionId!,
        ),
      select: selectTransactionCustomFields,
      enabled: transactionId !== undefined && transactionId !== '',
    }),
}

export const useQueryExpenseCategories = ({
  filters,
  additionalKey,
  queryCacheOptions,
  ...rest
}: QueryExpenseCategoriesOptions) => {
  const query = useQuery({
    ...expenseCategoryQueries.list(filters, additionalKey),
    ...queryCacheOptions,
    ...rest,
  })
  return { ...query, error: toBackendError(query.error) }
}

interface QueryExpenseCategoryOptions
  extends ExtendBaseQueryOptions<ExpenseCategoryResponse> {
  expenseCategoryId: string | undefined
}

export const useQueryExpenseCategory = ({
  expenseCategoryId,
  enabled = true,
  ...rest
}: QueryExpenseCategoryOptions) => {
  const query = useQuery({
    ...expenseCategoryQueries.detail(expenseCategoryId),
    ...rest,
    enabled: enabled && expenseCategoryId !== undefined,
  })
  return { ...query, error: toBackendError(query.error) }
}

export type PostExpenseCategoryRequest = {
  name: string
  code: string
}

export const usePostExpenseCategory = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ExpenseCategoryResponse,
    UseExtendMutationVariablesBody
  >(MutationMethod.POST, {
    mutationUrl: () => `/expensedata/categories`,
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: [EXPENSE_DATA_QK, CATEGORIES_QK],
      })
    },
  })
}

export type PatchExpenseCategoryRequest = {
  name?: string
  active?: boolean
  requiredForVirtualCards?: boolean
  requiredForTransactions?: boolean
  freeTextAllowed?: boolean
  integratorEnabled?: boolean
  integratorFieldNumber?: number
}

export const usePatchExpenseCategory = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ExpenseCategoryResponse,
    { id: string; body: PatchExpenseCategoryRequest }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ id }) => `/expensedata/categories/${id}`,
    onSuccess: (data, variables) => {
      // update list reponses in place so the exp cat doesnt jump around in the list
      const currentQueryKeys = getListQueryKeys(
        queryClient,
        [EXPENSE_DATA_QK, CATEGORIES_QK],
        [EXPENSE_CATEGORIES_LABELS_QK],
      )
      currentQueryKeys.forEach((queryKey) => {
        queryClient.setQueryData(
          queryKey,
          (oldExpCats: { expenseCategories: ExpenseCategoryResponse[] }) => {
            if (oldExpCats === undefined) return undefined
            return {
              expenseCategories: oldExpCats.expenseCategories.map((expCat) =>
                expCat.id === data.id ? data : expCat,
              ),
            }
          },
        )
      })
      // update detail response in place (no need for extra call when the patch returns the full object)
      queryClient.setQueryData(
        orgQueryKeys.expenseDataDetail(variables.id),
        () => data,
      )
    },
  })
}

export interface ExpenseCategoryLabelResponse {
  id: string
  name: string
  code: string
  active: boolean
  subName?: string
  categoryId: string
}

interface ExpenseCategoryLabelsResponse {
  pagination: PaginationResponse
  expenseLabels: ExpenseCategoryLabelResponse[]
}

export enum ExpenseCategoryLabelSortField {
  ActiveCodeAsc = 'activeCodeAsc',
  ActiveCodeDesc = 'activeCodeDesc',
  ActiveLabelNameAsc = 'activeLabelNameAsc',
  ActiveLabelNameDesc = 'activeLabelNameDesc',
  // use these with SortDirection
  Name = 'name',
  Code = 'code',
}

export type ExpenseCategoryLabelFiltersBody = {
  search?: string
  active?: boolean
  requiredForVirtualCards?: boolean
  requiredForTransactions?: boolean
  count?: number
  sortField?: ExpenseCategoryLabelSortField
  sortDirection?: 'ASC' | 'DESC'
}

export const activeAlphabeticalExpenseCategoryLabelFiltersBody: ExpenseCategoryLabelFiltersBody =
  {
    active: true,
    sortField: ExpenseCategoryLabelSortField.Name,
    sortDirection: 'ASC',
    count: 50,
  }

interface QueryExpenseCategoryLabelsOptions
  extends ExtendBaseInfiniteQueryOptions<
    ExpenseCategoryLabelsResponse,
    number,
    ExpenseCategoryLabelResponse[]
  > {
  expenseCategoryId: string | undefined
  filters: ExpenseCategoryLabelFiltersBody
  additionalKey?: string
}

export const useQueryExpenseCategoryLabels = ({
  expenseCategoryId,
  filters,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryExpenseCategoryLabelsOptions) => {
  return useExtendInfiniteQuery({
    queryKey: orgQueryKeys.expenseDataLabels(
      expenseCategoryId,
      filters,
      rest.additionalKey,
    ),
    queryUrl: ({ pageParam }) =>
      `/expensedata/categories/${expenseCategoryId}/labels?${queryString.stringify({ page: pageParam, ...filters }, { skipEmptyString: true })}`,
    enabled: enabled && expenseCategoryId !== undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
    select: (data) => data.pages.flatMap((page) => page.expenseLabels),
    ...queryCacheOptions,
    ...rest,
  })
}

interface QueryExpenseCategoryLabelOptions
  extends ExtendBaseQueryOptions<ExpenseCategoryLabelResponse> {
  expenseCategoryId: string | undefined
  labelId: string | undefined
}

export const useQueryExpenseCategoryLabel = ({
  expenseCategoryId,
  labelId,
  enabled = true,
  ...rest
}: QueryExpenseCategoryLabelOptions) => {
  const query = useQuery({
    ...expenseCategoryQueries.label(expenseCategoryId, labelId),
    ...rest,
    enabled:
      enabled && expenseCategoryId !== undefined && labelId !== undefined,
  })
  return { ...query, error: toBackendError(query.error) }
}

export interface QueryExpenseCategoryLabelDetails
  extends ExtendBaseQueryOptions<ExpenseCategoryLabelResponse> {
  expenseCategoryId: string | undefined
  labelId: string | undefined
}

interface PatchExpenseCategoryLabelRequest {
  name?: string
  active?: boolean
}

export interface PatchExpenseCategoryLabelVariables {
  categoryId: string
  labelId: string
  body: PatchExpenseCategoryLabelRequest
}

export const usePatchExpenseCategoryLabel = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ExpenseCategoryLabelResponse,
    PatchExpenseCategoryLabelVariables
  >(MutationMethod.PATCH, {
    mutationKey: orgQueryKeys.patchExpenseDataLabel(),
    mutationUrl: ({ categoryId, labelId }) =>
      `/expensedata/categories/${categoryId}/labels/${labelId}`,
    onSuccess: (data, variables) => {
      // update list responses in place so the exp cat doesn't jump around in the list
      const currentQueryKeys = getListQueryKeys(queryClient, [
        ...orgQueryKeys.expenseDataDetail(variables.categoryId),
        EXPENSE_CATEGORIES_LABELS_QK,
      ])
      currentQueryKeys.forEach((queryKey) => {
        queryClient.setQueryData(
          queryKey,
          (oldData?: InfiniteData<ExpenseCategoryLabelsResponse, number>) => {
            if (oldData === undefined) return undefined
            return produce(oldData, (draft) => {
              draft.pages = oldData.pages.map((page) => ({
                pagination: page.pagination,
                expenseLabels: page.expenseLabels.map((expCatLabel) =>
                  expCatLabel.id === data.id ? data : expCatLabel,
                ),
              }))
            })
          },
        )
      })
    },
  })
}

export type PostExpenseCategoryLabelRequest = {
  name: string
  code: string
  active?: boolean
}

export const usePostExpenseCategoryLabel = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ExpenseCategoryLabelResponse,
    { categoryId: string; body: PostExpenseCategoryLabelRequest }
  >(MutationMethod.POST, {
    mutationUrl: ({ categoryId }) =>
      `/expensedata/categories/${categoryId}/labels`,
    onSuccess: (data, variables) => {
      // update list responses in place so the exp cat doesn't jump around in the list
      const currentQueryKeys = getListQueryKeys(queryClient, [
        ...orgQueryKeys.expenseDataDetail(variables.categoryId),
        EXPENSE_CATEGORIES_LABELS_QK,
      ])
      currentQueryKeys.forEach((queryKey) => {
        queryClient.setQueryData(
          queryKey,
          (oldData?: InfiniteData<ExpenseCategoryLabelsResponse, number>) => {
            if (oldData === undefined) return undefined
            return produce(oldData, (draft) => {
              draft.pages = oldData.pages.map((page) => ({
                pagination: page.pagination,
                expenseLabels: [data, ...page.expenseLabels],
              }))
            })
          },
        )
      })
    },
  })
}

export const queryExpenseCategoryLabelsBulkTemplate = async (
  queryClient: QueryClient,
  config: AxiosRequestConfig,
  setIsQueryPending: (isPending: boolean) => void,
) => {
  try {
    setIsQueryPending(true)
    const response = await queryClient.fetchQuery({
      queryKey: [EXPENSE_CATEGORIES_LABELS_BULK_TEMPLATE_QK],
      queryFn: () =>
        axios.get<string>(`/expensedata/bulkexpenselabelcsvtemplate`, config),
      retry: false,
    })

    if (response) {
      const pom = document.createElement('a')
      document.body.appendChild(pom)
      const blob = new Blob([response.data], { type: 'text/csv' })
      pom.href = window.URL.createObjectURL(blob)
      pom.download = 'Expense Category Labels Template'
      pom.click()
      document.body.removeChild(pom)
    }
  } catch (e) {
    // TODO: implement better error handling/expose error to front-end
    /* eslint-disable-next-line no-console */
    console.warn(`Could not get Expense Category Labels Bulk Template`, e)
  } finally {
    setIsQueryPending(false)
  }
}

export const usePostBulkExpenseCategoryLabels = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    { count: number },
    { categoryId: string; body: FormData }
  >(MutationMethod.POST, {
    mutationUrl: ({ categoryId }) =>
      `/expensedata/categories/${categoryId}/labels/csvbulkpush`,
    requestConfig: RequestConfig.Form,
    onSuccess: (data, variables) => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: [
          ...orgQueryKeys.expenseDataDetail(variables.categoryId),
          EXPENSE_CATEGORIES_LABELS_QK,
        ],
      })
    },
  })
}

interface QueryTransactionCustomFieldsOptions
  extends ExtendBaseQueryOptions<
    { expenseCategories: ExpenseCategoryResponse[] },
    AdditionalFieldsResponse[]
  > {
  transactionId: string | undefined
}

export const useQueryTransactionCustomFields = ({
  transactionId,
  ...rest
}: QueryTransactionCustomFieldsOptions) => {
  const query = useQuery({
    ...expenseCategoryQueries.transactionCustomFields(transactionId),
    ...rest,
  })
  return { ...query, error: toBackendError(query.error) }
}

/**
 * TODO: there is a larger data modelling problem on the backend that needs to be solved
 * where we don't get the category ID for Customer and Vendor expense categories
 * so we have to map them based on the categoryType on the front-end instead.
 */
export const getStampedLabelForExpCat = (
  transaction: TransactionListModel,
  expenseCategory: ExpenseCategoryResponse,
  showReconnectedErpExpenseCategoryLabels = false,
) => {
  let matchingExpenseCat: ExpenseDetailsModel | undefined
  switch (expenseCategory.type) {
    case ExpenseCategoryType.Customer:
      matchingExpenseCat = transaction.expenseDetails?.find(
        (expCat) => expCat.categoryType === ExpenseCategoryType.Customer,
      )
      break
    case ExpenseCategoryType.Vendor:
      matchingExpenseCat = transaction.expenseDetails?.find(
        (expCat) => expCat.categoryType === ExpenseCategoryType.Vendor,
      )
      break
    default:
      matchingExpenseCat = transaction.expenseDetails?.find(
        (expCat) => expCat.categoryId === expenseCategory.id,
      )
  }

  if (
    showReconnectedErpExpenseCategoryLabels &&
    matchingExpenseCat === undefined
  ) {
    matchingExpenseCat = transaction.expenseDetails?.find(
      (expCat) =>
        (expCat.categoryArchivedAt !== undefined ||
          expCat.labelArchivedAt !== undefined) &&
        expCat.categoryType === expenseCategory.type &&
        expCat.categoryName === expenseCategory.name,
    )
  }

  return matchingExpenseCat
}
